import {
  isInternalUser,
  isUserRoleGreater,
  isUserRoleLess,
  isYardDriverUser,
  UserRole
} from '@nova/core';

export default {
  registerRoutes(routes, newRoutes) {
    newRoutes.forEach(mRoutes => {
      let registeredRoutePaths = routes.map(route => route.name);
      let newRoutesToRegister = mRoutes.filter(routeToRegister => {
        if (!registeredRoutePaths.includes(routeToRegister.name)) {
          return routeToRegister;
        }
      });
      if (newRoutesToRegister) {
        routes = [...routes, ...newRoutesToRegister];
      }
    });

    return routes;
  },
  /**
   * Open to any better way to do this, but this is needed in order to prevent weird blips of components rendered based on authentication
   * i.e. Not authenticated when app is initially rendered, then authentication finishes causes the org warning to flash on the screen on load
   */
  setAppLoadedTrue(store) {
    if (!store.getters['Auth/appLoaded']) {
      store.commit('Auth/setAppLoaded', true);
    }
  },
  // eslint-disable-next-line no-unused-vars
  async checkDirty(store, from) {
    if (store.$app.config.globalProperties.checkDirtyConfirmation && from.meta.isDirty) {
      if (!(await store.$app.config.globalProperties.checkDirtyConfirmation())) {
        return true;
      }
    }

    return false;
  },
  isAuthRequired(route) {
    return route.meta.requiresAuth || typeof route.meta.requiresAuth === 'undefined';
  },
  async isUserAuthorized(store, getOrg = true) {
    const token = sessionStorage?.getItem('access_token') || localStorage?.getItem('access_token');
    let user = await store.getters['Auth/me'];
    let authorized = token && user;

    if (!authorized && token) {
      try {
        authorized = await store.dispatch('Auth/getMe', getOrg).then(r => r.data.id);
      } catch (e) {
        authorized = false;
      }
    }

    return authorized;
  },
  setPageTitle(store, to, appName) {
    if (!to.meta.preventTitleReset) {
      store.commit('navigation/setPageTitle', null);
    }

    const rawTitle = typeof to.meta.title === 'function' ? to.meta.title() : to.meta.title;
    document.title = rawTitle ? `${rawTitle} - ${appName}` : appName;
  },
  isDisabledForUserRole(store, route) {
    const currentUser = store?.state?.Auth?.me;
    // We can't block pages if we don't have user context, but we should always have it
    if (!currentUser) {
      return false;
    }

    if (route.meta?.maxRole && isUserRoleGreater(currentUser.role, route.meta.maxRole)) {
      return true;
    }

    if (route.meta?.minRole && isUserRoleLess(currentUser.role, route.meta.minRole)) {
      return true;
    }

    return false;
  },
  isDisabledForWarehouseUser(store, route) {
    const currentUser = store?.state?.Auth?.me;

    if (!currentUser) {
      return false;
    }
    if (route.meta.isWarehouseRestricted) {
      console.log(route);
    }

    return false;
  },
  isDisabledForUser(store, route) {
    return (
      this.isDisabledForUserRole(store, route) || this.isDisabledForWarehouseUser(store, route)
    );
  },
  getHomepageNameForUser(store) {
    return isInternalUser(store?.state?.Auth?.me) ? 'internalHome' : 'home';
  },
  /**
   * Checks if user is a yard driver and redirects them to Circe if so.
   *
   * @param {Object} params - Parameters object
   * @param {{id: string, role: string, email?: string, orgId?: string, [key: string]: *}|undefined|null} params.user - The user object from Auth/me with at minimum id and role properties
   * @param {string} params.circeUrl - The Circe URL to redirect to
   * @param {string|null|undefined} [params.accessToken] - Optional access token (from route params). If not provided, will read from storage.
   * @returns {boolean} Returns true if redirect occurred, false otherwise
   */
  redirectYardDriverToCirce({ user, circeUrl, accessToken = null }) {
    if (!user || !isYardDriverUser(user)) {
      return false;
    }

    const token =
      accessToken || sessionStorage.getItem('access_token') || localStorage.getItem('access_token');

    if (!token || !circeUrl) {
      console.warn('Cannot redirect yard driver to Circe: missing token or circeUrl');
      return false;
    }

    window.location.replace(`${circeUrl}?token=${token}`);
    return true;
  },
  /**
   * Checks if user is a carrier and redirects them to the scheduling portal (Luna) if so.
   * Carriers belong to the scheduling portal, so when one is authenticated in the warehouse
   * portal (Helios) we hand the session token to Luna (its router auto-logs-in from ?token=).
   *
   * @param {Object} params - Parameters object
   * @param {{id: string, role: string, orgId?: string, [key: string]: *}|undefined|null} params.user - The user object from Auth/me with at minimum id and role properties
   * @param {string} params.lunaUrl - The scheduling portal (Luna) URL to redirect to
   * @param {string|null|undefined} [params.accessToken] - Optional access token (from route params). If not provided, will read from storage.
   * @param {boolean} [params.useHandoffCode] - When true, mint a single-use handoff code and redirect with ?handoff=1 (the code is exchanged for the token server-side via an HttpOnly cookie). When false (default), use the legacy ?token= redirect. The caller decides this from its feature flag.
   * @returns {Promise<boolean>} Resolves true if a redirect occurred, false otherwise.
   */
  async redirectCarrierToScheduling({ user, lunaUrl, accessToken = null, useHandoffCode = false }) {
    // Detect by carrier ROLE only (not isCarrierUser, which also requires an empty orgId). Some
    // carrier accounts have an orgId, and they still belong to the scheduling portal. Use the exact
    // CARRIER/CARRIER_ADMIN pair (NOT CARRIER_ROLES, which also includes BASE warehouse-side users),
    // mirroring the original check on the (now removed) public carrier-exists endpoint.
    if (!user || ![UserRole.CARRIER, UserRole.CARRIER_ADMIN].includes(user.role)) {
      return false;
    }

    const token =
      accessToken || sessionStorage.getItem('access_token') || localStorage.getItem('access_token');

    if (!token || !lunaUrl) {
      console.info('Cannot redirect carrier to scheduling portal: missing token or lunaUrl');
      return false;
    }

    if (!useHandoffCode) {
      // Legacy path: hand the session token to Luna via the URL (its router auto-logs-in from
      // ?token=). Clear the warehouse session first so we don't bounce back here on the next visit.
      localStorage.removeItem('access_token');
      sessionStorage.removeItem('access_token');
      window.location.replace(`${lunaUrl}?token=${token}`);
      return true;
    }

    // Mint the handoff code BEFORE clearing the session: if it fails we keep the session intact and
    // abort, rather than stranding the carrier. `withCredentials` lets the browser store the cookie.
    try {
      await axios.post(
        'auth/handoff-code',
        { target: 'luna' },
        // The carrier session isn't persisted on this portal, so the token isn't on the shared axios
        // client; send it as a one-off header.
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
    } catch (error) {
      // Log only sanitized fields — the raw Axios error carries the request config, including the
      // Authorization header (the carrier's token).
      console.error('Failed to mint carrier scheduling-portal handoff code', {
        status: error?.response?.status,
        message: error?.message
      });
      return false;
    }

    // Drop the carrier session so we don't bounce back here on the next visit (e.g. after the user
    // logs out of Luna). ?handoff (not a token) tells Luna to exchange the cookie server-side.
    localStorage.removeItem('access_token');
    sessionStorage.removeItem('access_token');

    window.location.replace(`${lunaUrl}?handoff=1`);
    return true;
  }
};
